import type { ReactNode } from 'react';
import { useCallback, useEffect, useId, useRef } from 'react';

import { useT } from '../../i18n';
import { Button } from './button';

/**
 * Anything that can hold focus inside the drawer. Kept in sync with `confirm.tsx`, which has
 * had a working trap since UX-211.
 */
const FOCUSABLE =
  'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

/**
 * Is the element actually focusable *on screen*?
 *
 * QA 2026-09-15 **UX-531**: the CSV-import drawer was the one drawer that never took focus,
 * because its first `input` is the `className="hidden"` file picker — `display:none`, so
 * `.focus()` is a silent no-op and the `stops()[0]` / heading fallbacks below never ran. Focus
 * stayed on the button behind the scrim while the panel claimed `aria-modal="true"`.
 * The test is `display`/`visibility` rather than `offsetParent`, which is null for every element
 * in jsdom and would make the trap skip the whole panel under unit test.
 */
function isVisible(el: HTMLElement): boolean {
  if (el.hidden) return false;
  const style = el.ownerDocument.defaultView?.getComputedStyle(el);
  return !style || (style.display !== 'none' && style.visibility !== 'hidden');
}

/**
 * Puts the caret before the first character and scrolls a single-line input back to its start.
 * `setSelectionRange` throws on the input types without a selection (email, number…), where only
 * the scroll reset applies.
 */
function showStart(input: HTMLInputElement): void {
  try {
    input.setSelectionRange(0, 0);
  } catch {
    // No selection API on this input type.
  }
  input.scrollLeft = 0;
}

/**
 * Right-hand editing drawer (Figma CMS-05): a fixed panel over a dimmed canvas with a sticky
 * footer for the actions.
 *
 * QA 2026-09-15 **UX-501**: it declared `role="dialog" aria-modal="true"` and then left focus on
 * the button behind it — fourteen consecutive Tabs after opening "+ Nuevo grant" landed on the
 * list underneath, never on the nine-field form the operator was looking at, and a screen reader
 * was told the page behind the scrim was inert when it was not. It now does what the confirm
 * dialog already did: focus moves in on open (the first field, or the heading when the drawer
 * has none), Tab is trapped inside, and focus returns to whatever opened it on close.
 */
export function Drawer({
  open,
  title,
  subtitle,
  onClose,
  footer,
  width = 440,
  children,
}: {
  open: boolean;
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  /** Max width in px — the CSV dry-run panel of CMS-09 is wider than an editor drawer. */
  width?: number;
  children: ReactNode;
}) {
  const { t } = useT();
  const titleId = useId();
  const panel = useRef<HTMLElement>(null);
  const opener = useRef<Element | null>(null);

  const stops = useCallback(
    () =>
      Array.from(panel.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []).filter(isVisible),
    [],
  );

  // Focus in on open, back to the opener on close. The heading is the fallback target so a
  // read-only drawer still announces itself instead of leaving focus on the page behind.
  useEffect(() => {
    if (!open) return;
    opener.current = document.activeElement;
    const fields = Array.from(
      panel.current?.querySelectorAll<HTMLElement>(
        'input:not([disabled]), select:not([disabled]), textarea:not([disabled])',
      ) ?? [],
    ).filter(isVisible);
    // The header ✕ stays a tab stop but is never the *entry* point: a drawer whose only visible
    // control is the close button would otherwise open on "Cerrar".
    const body = stops().filter((el) => !el.hasAttribute('data-drawer-close'));
    const first =
      fields[0] ??
      body[0] ??
      stops()[0] ??
      panel.current?.querySelector<HTMLElement>('[data-drawer-title]');
    first?.focus();
    // UI review 2026-10-05 (cms-06): a long prefilled name opened scrolled to its END — the
    // value is set before focus, which leaves the caret (and the scroll) at the last character,
    // so the operator read "…s para cantar toda la noche". Open on the start of the value.
    if (first instanceof HTMLInputElement) showStart(first);
    return () => {
      const back = opener.current;
      opener.current = null;
      if (back instanceof HTMLElement && back.isConnected) back.focus();
    };
  }, [open, stops]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !panel.current) return;
      const list = stops();
      if (list.length === 0) return;
      const first = list[0]!;
      const last = list[list.length - 1]!;
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !panel.current.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !panel.current.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, stops]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-30 flex justify-end">
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-ink/25"
        onClick={onClose}
        data-testid="drawer-scrim"
      />
      <aside
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={{ maxWidth: `${width}px` }}
        className="relative flex h-full w-full flex-col bg-panel"
      >
        <header className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <h2
              id={titleId}
              data-drawer-title
              tabIndex={-1}
              className="text-[17px] font-bold text-ink outline-none"
            >
              {title}
            </h2>
            {subtitle ? <p className="mt-0.5 truncate text-[12px] text-muted">{subtitle}</p> : null}
          </div>
          <Button
            data-drawer-close
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label={t('common:actions.close')}
          >
            ✕
          </Button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer ? (
          <footer className="flex items-center justify-end gap-2 border-t border-line px-5 py-3">
            {footer}
          </footer>
        ) : null}
      </aside>
    </div>
  );
}
